import { AxiosInstance } from "axios";
import { uploadFilePart } from "../../store/utils/fetch";
import { multipartUploadProgressHandler } from "../../utils/fileUtils";
import {
  SUPPLY_CHAIN_CANCEL_UPLOAD,
  SUPPLY_CHAIN_CONFIRM_CHUNK_UPLOAD,
  SUPPLY_CHAIN_FINALIZE_UPLOAD,
  SUPPLY_CHAIN_GET_PRESIGNED_URL,
  SUPPLY_CHAIN_UPLOAD_START,
} from "../../store/utils/routes";
import {
  SupplyChainFileType,
  SupplyChainRelatedType,
} from "./postSupplyChainUpdateFile";

enum ContentTypeEnum {
  AudioMpeg = "audio/mpeg",
  VideoMp4 = "video/mp4",
  VideoQuicktime = "video/quicktime",
  AudioXFlac = "audio/x-flac",
  AudioXAiff = "audio/x-aiff",
  AudioWave = "audio/wave",
  ApplicationPdf = "application/pdf",
  ImageJpeg = "image/jpeg",
  ImagePJpeg = "image/pjpeg",
  ImagePng = "image/png",
  VideoH264 = "video/H264",
  VideoMp2p = "video/MP2P",
  VideoMpeg = "video/mpeg",
  AudioXWav = "audio/x-wav",
  AudioWav = "audio/wav",
  AudioMpeg3 = "audio/mpeg3",
  AudioMp3 = "audio/mp3",
  AudioFlac = "audio/flac",
  AudioMp4 = "audio/mp4",
  FontTtf = "font/ttf",
  FontWoff = "font/woff",
  FontWoff2 = "font/woff2",
  TextCsv = "text/csv",
}

// Request DTOs
interface CreateMultipartUploadDto {
  fileName: string; // Original file name
  fileSize: number; // File size in bytes
  contentType: ContentTypeEnum;
  fileType: SupplyChainFileType;
  relatedType: SupplyChainRelatedType;
}
interface GetPresignedUrlDto {
  uploadId: string;
  partNumber: number; // 1-based chunk index
}
interface ConfirmChunkUploadDto {
  uploadId: string;
  partNumber: number;
  eTag: string; // ETag received from S3
}
interface FinalizeMultipartUploadDto {
  uploadId: string;
  contentType: ContentTypeEnum;
}
// Response Types
interface CreateMultipartUploadResponse {
  uploadId: string;
  tmpFilePath: string;
  totalChunks: number;
  chunkSize: number;
  message: string;
}
interface GetPresignedUrlResponse {
  signedUrl: string;
  message: string;
}
export interface FinalizeMultipartUploadResponse {
  signedUrl: string;
  finalFilePath: string;
  message: string;
}

const supplyChainUploadStart = async (
  payload: CreateMultipartUploadDto,
  axiosInstance: AxiosInstance,
): Promise<CreateMultipartUploadResponse> => {
  try {
    const resp = await axiosInstance.post(SUPPLY_CHAIN_UPLOAD_START, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return resp.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const getPresignedUrl = async (
  payload: GetPresignedUrlDto,
  axiosInstance: AxiosInstance,
): Promise<GetPresignedUrlResponse> => {
  try {
    const resp = await axiosInstance.post(
      SUPPLY_CHAIN_GET_PRESIGNED_URL,
      payload,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return resp.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const confirmChunkUpload = async (
  payload: ConfirmChunkUploadDto,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  try {
    const resp = await axiosInstance.post(
      SUPPLY_CHAIN_CONFIRM_CHUNK_UPLOAD,
      payload,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    if (resp.status >= 300) {
      throw new Error("Failed to confirm chunk upload");
    }
    return;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const finalizeUpload = async (
  payload: FinalizeMultipartUploadDto,
  axiosInstance: AxiosInstance,
): Promise<FinalizeMultipartUploadResponse> => {
  try {
    const resp = await axiosInstance.post(
      SUPPLY_CHAIN_FINALIZE_UPLOAD,
      payload,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return resp.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const supplyChainCancelMultipartUpload = (
  uploadId: string,
  axiosInstance: AxiosInstance,
) => {
  return axiosInstance.post(SUPPLY_CHAIN_CANCEL_UPLOAD, {
    uploadId,
  });
};

const supplyChainUploadChunk = async (
  file: File,
  uploadId: string,
  partNumber: number,
  chunkSize: number,
  offset: number,
  progressHandler: (progress: number) => void,
  axiosInstance: AxiosInstance,
  abortSignal?: AbortSignal,
) => {
  const { signedUrl } = await getPresignedUrl(
    { uploadId, partNumber },
    axiosInstance,
  );
  const chunk = file.slice(offset, offset + chunkSize);
  const eTag = await uploadFilePart(
    chunk,
    signedUrl,
    progressHandler,
    abortSignal,
  );
  await confirmChunkUpload({ uploadId, partNumber, eTag }, axiosInstance);
};

export const supplyChainUploadFileToS3 = async (
  file: File,
  supplyChainFileType: SupplyChainFileType,
  supplyChainRelatedType: SupplyChainRelatedType,
  axiosInstance: AxiosInstance,
  setProgressState?: (percent: number) => void,
  abortSignal?: AbortSignal,
): Promise<FinalizeMultipartUploadResponse> => {
  const { uploadId, chunkSize, totalChunks } = await supplyChainUploadStart(
    {
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type as ContentTypeEnum,
      fileType: supplyChainFileType,
      relatedType: supplyChainRelatedType,
    },
    axiosInstance,
  );

  const progressHandler = multipartUploadProgressHandler(
    totalChunks,
    file.size,
    setProgressState,
  );

  try {
    for (let i = 0; i < totalChunks; i++) {
      await supplyChainUploadChunk(
        file,
        uploadId,
        i + 1,
        chunkSize,
        i * chunkSize,
        progressHandler(i),
        axiosInstance,
        abortSignal,
      );
    }
  } catch (e) {
    await supplyChainCancelMultipartUpload(uploadId, axiosInstance);
    throw e;
  }

  return await finalizeUpload(
    {
      uploadId,
      contentType: file.type as ContentTypeEnum,
    },
    axiosInstance,
  );
};
